import { useEffect, useRef } from "react";
import {
  DrawioEvent,
  DrawioInboundMessage,
  parseDrawioMessage,
} from "./drawioProtocol";

/**
 * Callbacks for drawio iframe events. All handlers are optional; the hook
 * only invokes the ones that are provided.
 */
export interface DrawioMessageHandlers {
  onInit?: () => void;
  onAutosave?: (xml: string) => void;
  onSave?: (xml: string) => void;
  /** PNG export response — receives the data: URL drawio renders into. */
  onPngExport?: (dataUrl: string) => void;
  /** XML export response — receives the serialized diagram XML. */
  onXmlExport?: (xml: string) => void;
}

/**
 * Installs a `message` listener on `window` that parses drawio postMessage
 * frames and dispatches to the provided handlers. Handlers are read through
 * a ref, so updating them between renders does not reattach the listener.
 *
 * Only messages from the configured drawio origin are processed; all others
 * are ignored by `parseDrawioMessage`.
 */
export function useDrawioMessaging(handlers: DrawioMessageHandlers): void {
  const handlersRef = useRef(handlers);

  // Synchronous assignment during render — ensures the ref is always
  // current when the listener fires. Updating in useEffect would leave a
  // gap between render commit and effect execution during which messages
  // would dispatch to the previous handlers (React useEvent RFC pattern).
  handlersRef.current = handlers;

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const parsed = parseDrawioMessage(event);
      if (!parsed) return;
      dispatchDrawioMessage(parsed, handlersRef.current);
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, []);
}

function dispatchDrawioMessage(
  message: DrawioInboundMessage,
  handlers: DrawioMessageHandlers,
): void {
  switch (message.event) {
    case DrawioEvent.INIT:
      handlers.onInit?.();
      break;
    case DrawioEvent.AUTOSAVE:
      if (typeof message.xml === "string") {
        handlers.onAutosave?.(message.xml);
      }
      break;
    case DrawioEvent.SAVE:
      if (typeof message.xml === "string") {
        handlers.onSave?.(message.xml);
      }
      break;
    case DrawioEvent.EXPORT: {
      // PNG exports arrive in `data` (a data: URL); XML exports in `xml`.
      const data = (message as { data?: unknown }).data;
      if (typeof data === "string") {
        handlers.onPngExport?.(data);
      } else if (typeof message.xml === "string") {
        handlers.onXmlExport?.(message.xml);
      }
      break;
    }
  }
}
